import * as React from "react";
import { useMemo } from "react";

import { DetailsHeader } from "components/app_layout/DetailsLayout";
import FrameworkStage from "components/framework/FrameworkStage";
import { RouterBreadcrumb } from "components/ui/breadcrumb/SimpleBreadcrumb";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { TextHighlighter } from "lib/core_components/TextHighlighter";
import { FrameworkDetails } from "lib/hooks/api/frameworks/useDescribeFramework";
import VerifiedProviderIcon from "lib/icons/VerifiedProviderIcon";
import { FrameworkStages } from "lib/types/graphQLEnums";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { ScheduleReminderButton } from "../reminders/ScheduleReminderButton";

import css from "./FrameworkHeader.module.scss";

type Props = {
  framework: FrameworkDetails | undefined;
  titleHighlight?: string[];
};

export default function FrameworkHeader({ framework, titleHighlight }: Props): JSX.Element {
  return (
    <DetailsHeader className={css.headerContent} borderBottom={false}>
      <RouterBreadcrumb
        overrides={
          framework ? { [`/frameworks/${framework.id}`]: { label: framework.title } } : undefined
        }
      />
      {framework ? (
        <div className={css.titleContainer}>
          <div aria-label="framework-header-content">
            <FrameworkHeaderStage framework={framework} />
            <div className={css.title}>
              {/* TODO: Turn this on when tested on db with frameworks */}
              {/* <Flex gap={8} alignItems="center"> */}
              <h1>
                <TextHighlighter content={framework?.title} wordsToHighlight={titleHighlight} />
              </h1>
              {framework?.validatedByProvider && (
                <VerifiedProviderIcon className={css.icons} ariaLabel="framework-verified-icon" />
              )}
              {/* <BetaIcon BetaFeature="frameworks" /> */}
              {/* </Flex> */}
            </div>
          </div>
          <div className={css.rightSide}>
            <ScheduleReminderButton
              dataType="framework"
              dataId={framework.id}
              dataTitle={framework.title}
              anchorDate={getFrameworkEndDate(framework)}
            />
          </div>
        </div>
      ) : (
        <CentredSpinner />
      )}
    </DetailsHeader>
  );
}

type FrameworkHeaderStageProps = {
  framework: FrameworkDetails;
};

function getFrameworkEndDate(framework: FrameworkDetails) {
  if (
    [FrameworkStages.Awarded, FrameworkStages.Live, FrameworkStages.Expired].includes(
      framework.stage,
    ) &&
    framework.contractPeriod?.endDate
  ) {
    return framework.contractPeriod.endDate;
  }
  if (framework.stage === FrameworkStages.Tendering && framework.tenderPeriod?.endDate) {
    return framework.tenderPeriod.endDate;
  }
}
/**
 * Displays stage of the framework and contextual date information
 * @param param0
 * @returns
 */
function FrameworkHeaderStage({ framework }: FrameworkHeaderStageProps): JSX.Element {
  const relativetimeContent = useMemo((): React.ReactNode => {
    if (
      [FrameworkStages.Awarded, FrameworkStages.Live, FrameworkStages.Expired].includes(
        framework.stage,
      ) &&
      framework.contractPeriod?.endDate
    ) {
      return (
        <>
          {/* Only doing it this way to ensure 4px gap */}
          <span>•</span>
          <span>
            {framework.stage === FrameworkStages.Expired ? "Ended" : "Ending"}{" "}
            {toHumanRelativeDateFromNow(new Date(framework.contractPeriod.endDate))}
          </span>
        </>
      );
    } else if (framework.stage === FrameworkStages.Tendering && framework.tenderPeriod?.endDate) {
      return (
        <>
          {/* Only doing it this way to ensure 4px gap */}
          <span>•</span>
          <span>
            Closing in {toHumanRelativeDateFromNow(new Date(framework.tenderPeriod.endDate))}
          </span>
        </>
      );
    }
  }, [framework.contractPeriod?.endDate, framework.stage, framework.tenderPeriod?.endDate]);

  return (
    <span className={css.stageAndRelativeTime}>
      <FrameworkStage stage={framework.stage} />
      {relativetimeContent}
    </span>
  );
}
